<template>
  <div class="card" style="min-height: calc(100vh - 180px)">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          :showAddNew="false"
          title="Device List"
      />
    </div>

    <div class="px-2">
      <DateQuerySetter
          btnTitle="Go"
          :isHideDate="true"
          @onClickGo="onFilterGo"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Status</label>
          <v-select
              placeholder="Select Status"
              v-model="status"
              :options="statusList"
              :reduce="item => item.value"
              label="name"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Search</label>
          <input v-model="searchTerm" type="text" class="form-control" placeholder="Device ID"/>
        </div>
      </DateQuerySetter>
    </div>

    <div class="col-12 px-2">
      <ListTable :devices="devices" @onToggleStatus="onToggleStatus"/>
    </div>

    <div class="px-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <GlobalLoader/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import GlobalLoader from '@/components/atom/GlobalLoader.vue'
import ListTable from '@/components/molecule/company/hr-payroll/device/ListTable.vue'
import handleHrPayroll from '@/services/modules/hrPayroll/hrPayroll'
import handleInventory from '@/services/modules/inventory'

const route = useRoute()
const router = useRouter()
const store = useStore()

const { fetchDeviceList, updateDeviceStatus } = handleHrPayroll()
const { fetchHome } = handleInventory()

const showError = inject('showError')
const showSuccess = inject('showSuccess')

const offset = ref(20)
const devices = ref([])
const status = ref(null)
const searchTerm = ref('')
const statusList = ref([])

const companyId = computed(() => route.params.companyId)
const page = computed(() => route.query.page)

function getQuery() {
  let query = '?company_id=' + companyId.value
  if (status.value) query += '&status=' + status.value
  if (searchTerm.value) query += '&search_term=' + searchTerm.value
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset.value
  return query
}

function getDeviceList() {
  return fetchDeviceList(getQuery()).then((res) => {
    if (!res.status) {
      devices.value = []
      return store.commit('resetPagination')
    }
    devices.value = res.data.data
    setPagination(res.data)
  })
}

function getStatusList() {
  return fetchHome().then((res) => {
    if (!res.status) return
    statusList.value = res.data.device_status
  })
}

function setPagination(data) {
  if (!data.data.length) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

async function onFilterGo() {
  await resetToFirstPage()
  getDeviceList()
}

function resetToFirstPage() {
  if (!route.query.page) return Promise.resolve()
  const routeQuery = Object.assign({}, route.query)
  delete routeQuery.page
  return router.push({ path: route.path, query: routeQuery })
}

async function onPageChange(pageNumber) {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = pageNumber
  await router.push({ path: route.path, query: routeQuery })
  getDeviceList()
}

function getNextStatus(currentStatus) {
  if (currentStatus === 'active') return 'inactive'
  return 'active'
}

function revertStatus(device, event, previousStatus) {
  device.status = previousStatus
  event.target.checked = previousStatus === 'active'
}

async function onToggleStatus(device, event) {
  const previousStatus = device.status
  const nextStatus = getNextStatus(previousStatus)
  const confirmed = window.confirm(`Are you sure you want to make this device ${nextStatus}?`)

  if (!confirmed) {
    event.target.checked = previousStatus === 'active'
    return
  }

  device.status = nextStatus
  const payload = {
    company_id: companyId.value,
    status: nextStatus
  }

  await updateDeviceStatus(device.id, payload).then((res) => {
    if (!res.status) {
      revertStatus(device, event, previousStatus)
      return showError(res.message)
    }
    return showSuccess(res.message)
  }).catch(() => {
    revertStatus(device, event, previousStatus)
    showError('Unable to update the device status.')
  })
}

onMounted(() => {
  getStatusList()
  getDeviceList()
})
</script>
